<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- 遮罩层 -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <!-- 弹窗主体 -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl transform transition-all">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">引入人详情</h3>
              <p v-if="referrerInfo" class="text-sm text-slate-500 mt-0.5">{{ referrerInfo.referrerName || referrerInfo.name }}</p>
            </div>
            <button
              @click="handleClose"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Tab 切换 -->
          <div class="flex-shrink-0 px-6 border-b border-slate-200">
            <nav class="flex gap-6">
              <button
                v-for="tab in tabs"
                :key="tab.key"
                @click="activeTab = tab.key"
                class="py-3 text-sm font-medium border-b-2 transition-colors"
                :class="activeTab === tab.key
                  ? 'text-primary-600 border-primary-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'"
              >
                {{ tab.label }}
                <span
                  v-if="tab.count"
                  class="ml-1.5 px-1.5 py-0.5 text-xs rounded-full"
                  :class="activeTab === tab.key ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-600'"
                >
                  {{ tab.count }}
                </span>
              </button>
            </nav>
          </div>

          <!-- 内容区域 -->
          <div class="flex-1 overflow-y-auto max-h-[60vh]">
            <!-- 加载状态 -->
            <div v-if="loading" class="flex items-center justify-center py-12">
              <div class="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            </div>

            <!-- 账户信息 Tab -->
            <div v-else-if="activeTab === 'account'" class="p-6">
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">累计收益</div>
                  <div class="text-xl font-semibold text-slate-900">¥{{ accountData.totalEarnings?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">已提现金额</div>
                  <div class="text-xl font-semibold text-slate-900">¥{{ accountData.totalWithdrawn?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">可提现余额</div>
                  <div class="text-xl font-semibold text-green-600">¥{{ accountData.availableBalance?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">冻结金额</div>
                  <div class="text-xl font-semibold text-amber-600">¥{{ accountData.frozenAmount?.toLocaleString() || '0' }}</div>
                </div>
              </div>

              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">账户类型</div>
                  <div class="font-medium text-slate-900">{{ accountData.accountTypeName || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">账户状态</div>
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="accountData.status === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  >
                    {{ accountData.statusName || '-' }}
                  </span>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">对公转账总额</div>
                  <div class="font-medium text-slate-900">¥{{ accountData.publicAccountTotal?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">平台代发总额</div>
                  <div class="font-medium text-slate-900">¥{{ accountData.platformPayoutTotal?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">创建时间</div>
                  <div class="font-medium text-slate-900">{{ formatTime(accountData.createTime) }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">更新时间</div>
                  <div class="font-medium text-slate-900">{{ formatTime(accountData.updateTime) }}</div>
                </div>
              </div>
            </div>

            <!-- 银行账户 Tab -->
            <div v-else-if="activeTab === 'bank'" class="p-6">
              <!-- 加载状态 -->
              <div v-if="bankLoading" class="flex items-center justify-center py-12">
                <div class="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              </div>

              <!-- 账户信息 -->
              <div v-else-if="lakalaReceiver" class="space-y-6">
                <!-- 账户状态卡片 -->
                <div class="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                      </div>
                      <div>
                        <div class="text-sm text-primary-800">分账接收方编号</div>
                        <div class="text-lg font-semibold text-primary-900">{{ lakalaReceiver.receiverNo || '-' }}</div>
                      </div>
                    </div>
                    <div class="flex flex-col items-end gap-2">
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="lakalaReceiver.status === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      >
                        {{ lakalaReceiver.status === 0 ? '正常' : '禁用' }}
                      </span>
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="lakalaReceiver.receiverStatus === 'VALID' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'"
                      >
                        {{ lakalaReceiver.receiverStatus === 'VALID' ? '已激活' : '未激活' }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="bg-slate-50 rounded-xl p-4">
                  <div class="flex items-center justify-between gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-slate-700">默认加盟商分账绑定</h4>
                      <p class="text-xs text-slate-500 mt-1">绑定需上传合作协议并等待拉卡拉审核</p>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="status-badge" :class="bindStatusClass">
                        {{ bindLoading ? '加载中...' : bindStatusText }}
                      </span>
                      <button
                        v-if="canSubmitBind"
                        @click="handleOpenBindModal"
                        class="btn-primary"
                      >
                        {{ bindActionText }}
                      </button>
                    </div>
                  </div>
                  <div v-if="bindRelation?.remark" class="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {{ bindRelation.remark }}
                  </div>
                </div>

                <!-- 基本信息 -->
                <div class="bg-slate-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-slate-700 mb-4">基本信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-slate-500 mb-1">分账接收方名称</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.receiverName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">联系电话</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.contactMobile || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户类型</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.acctTypeCode === '57' ? '对公账户' : lakalaReceiver.acctTypeCode === '58' ? '对私账户' : '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">提款类型</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.settleType === '01' ? '主动提款' : lakalaReceiver.settleType === '03' ? '交易自动结算' : '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 收款账户信息 -->
                <div class="bg-slate-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-slate-700 mb-4">收款账户信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-slate-500 mb-1">收款账户名称</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.acctName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">收款账号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ lakalaReceiver.acctNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">开户行名称</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.acctOpenBankName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">开户行号</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.acctOpenBankCode || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">清算行行号</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.acctClearBankCode || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户证件类型</div>
                      <div class="text-sm text-slate-900">{{ getCertificateTypeText(lakalaReceiver.acctCertificateType) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户证件号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ lakalaReceiver.acctCertificateNo || '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 对公账户信息 -->
                <div v-if="lakalaReceiver.acctTypeCode === '57'" class="bg-amber-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-amber-800 mb-4">营业执照及法人信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-amber-600 mb-1">营业执照号</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.licenseNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">营业执照名称</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.licenseName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人姓名</div>
                      <div class="text-sm text-slate-900">{{ lakalaReceiver.legalPersonName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人证件类型</div>
                      <div class="text-sm text-slate-900">{{ getLegalCertTypeText(lakalaReceiver.legalPersonCertificateType) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人证件号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ lakalaReceiver.legalPersonCertificateNo || '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 编辑按钮 -->
                <div class="flex justify-end">
                  <button
                    @click="handleEditLakala"
                    class="btn-primary"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                    编辑账户
                  </button>
                </div>
              </div>

              <!-- 空状态 - 未创建过分账账户 -->
              <div v-else class="flex flex-col items-center justify-center py-16 text-slate-500">
                <svg class="w-20 h-20 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
                <span class="text-lg mb-2">暂未创建分账账户</span>
                <span class="text-sm mb-6">点击下方按钮创建拉卡拉分账接收方账户</span>
                <button
                  @click="handleCreateLakala"
                  class="btn-primary"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  创建分账账户
                </button>
              </div>
            </div>

            <!-- 流水记录 Tab -->
            <div v-else-if="activeTab === 'flow'" class="p-6">
              <!-- 搜索筛选 -->
              <div class="flex flex-wrap gap-4 mb-4">
                <select
                  v-model="flowQuery.flowType"
                  class="form-select-native form-control-sm"
                >
                  <option value="">全部类型</option>
                  <option v-for="type in flowTypeOptions" :key="type.value" :value="type.value">{{ type.label }}</option>
                </select>
                <el-date-picker
                  v-model="flowQuery.startDate"
                  type="date"
                  format="YYYY年MM月DD日"
                  value-format="YYYY-MM-DD"
                  class="w-36 form-date form-control-sm"
                  placeholder="开始日期"
                />
                <el-date-picker
                  v-model="flowQuery.endDate"
                  type="date"
                  format="YYYY年MM月DD日"
                  value-format="YYYY-MM-DD"
                  class="w-36 form-date form-control-sm"
                  placeholder="结束日期"
                />
                <button
                  @click="handleFlowSearch"
                  class="btn-primary"
                >
                  搜索
                </button>
              </div>

              <!-- 流水列表 -->
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">类型</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">金额</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">变动前余额</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">变动后余额</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">操作人</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">备注</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">创建时间</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200">
                    <tr v-if="flowLoading">
                      <td colspan="7" class="px-4 py-12 text-center">
                        <div class="flex flex-col items-center justify-center">
                          <div class="w-6 h-6 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                          <span class="text-slate-500">加载中...</span>
                        </div>
                      </td>
                    </tr>
                    <tr v-else-if="flowList.length === 0">
                      <td colspan="7" class="px-4 py-12 text-center">
                        <div class="flex flex-col items-center justify-center">
                          <svg class="w-12 h-12 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                          </svg>
                          <span class="text-slate-500">暂无流水记录</span>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="flow in flowList"
                      :key="flow.id"
                      class="hover:bg-slate-50"
                    >
                      <td class="px-4 py-3">
                        <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getFlowTypeClass(flow.flowType)"
                        >
                          {{ flow.flowTypeName }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-right font-medium" :class="flow.amount >= 0 ? 'text-green-600' : 'text-red-600'">
                        {{ flow.amount >= 0 ? '+' : '' }}{{ flow.amount?.toLocaleString() }}
                      </td>
                      <td class="px-4 py-3 text-right text-slate-600">
                        ¥{{ flow.balanceBefore?.toLocaleString() || '0' }}
                      </td>
                      <td class="px-4 py-3 text-right text-slate-900 font-medium">
                        ¥{{ flow.balanceAfter?.toLocaleString() || '0' }}
                      </td>
                      <td class="px-4 py-3 text-slate-600">{{ flow.operator || '-' }}</td>
                      <td class="px-4 py-3 text-slate-600 max-w-[200px] truncate" :title="flow.remark">
                        {{ flow.remark || '-' }}
                      </td>
                      <td class="px-4 py-3 text-slate-600">{{ formatTime(flow.createTime) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 分页 -->
              <AppPagination
                v-if="flowList.length > 0"
                class="pagination-bridge"
                :current="flowPagination.current"
                :size="flowPagination.size"
                :total="flowTotal"
                :pages="flowPagination.pages"
                :page-sizes="[10, 20, 50]"
                @page-change="handleFlowPageChange"
                @size-change="handleFlowPageSizeChange"
              />
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button
              @click="handleClose"
              class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 拉卡拉分账账户弹窗 -->
    <ReferrerLakalaFormModal
      v-model:visible="showLakalaForm"
      :referrer-id="referrerId"
      :account-data="editingLakala"
      @success="handleLakalaSuccess"
    />

    <ReferrerLakalaBindModal
      v-model:visible="showBindModal"
      :referrer-id="referrerId"
      :referrer-info="accountData"
      :receiver="lakalaReceiver"
      :bind-relation="bindRelation"
      @success="handleBindSuccess"
    />
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import {
  getDefaultReferrerFranchiseeBind,
  getReferrerAccount,
  getReferrerFlowList,
  getReferrerLakalaReceiver
} from '@/api/commission'
import ReferrerLakalaBindModal from './ReferrerLakalaBindModal.vue'
import ReferrerLakalaFormModal from './ReferrerLakalaFormModal.vue'
import AppPagination from '@/components/AppPagination.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  referrerId: {
    type: [Number, String],
    default: null
  },
  referrerInfo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible'])

// Tab 配置
const tabs = computed(() => [
  { key: 'account', label: '账户信息', count: null },
  { key: 'bank', label: '分账账户', count: lakalaReceiver.value ? 1 : null },
  { key: 'flow', label: '流水记录', count: flowTotal.value }
])

const activeTab = ref('account')

// 加载状态
const loading = ref(false)
const flowLoading = ref(false)
const bankLoading = ref(false)
const bindLoading = ref(false)

// 账户数据
const accountData = ref({})

// 拉卡拉分账接收方账户
const lakalaReceiver = ref(null)
const bindRelation = ref(null)

// 拉卡拉分账账户弹窗
const showLakalaForm = ref(false)
const showBindModal = ref(false)
const editingLakala = ref(null)

// 流水列表
const flowList = ref([])
const flowTotal = ref(0)
const flowPagination = reactive({
  current: 1,
  size: 10,
  pages: 1
})

// 流水查询条件
const flowQuery = reactive({
  flowType: '',
  startDate: '',
  endDate: ''
})

// 流水类型选项
const flowTypeOptions = [
  { value: 1, label: '佣金收入' },
  { value: 2, label: '提现支出' },
  { value: 3, label: '提现退回' },
  { value: 4, label: '人工调整' }
]

const bindStatusText = computed(() => {
  const status = bindRelation.value?.bindStatus
  if (status === 0) return '审核中'
  if (status === 1) return '已绑定'
  if (status === 2) return '绑定拒绝'
  if (status === 3) return '解绑审核中'
  if (status === 4) return '已解绑'
  if (status === 5) return '解绑拒绝'
  return '未绑定'
})

const bindStatusClass = computed(() => {
  const status = bindRelation.value?.bindStatus
  if (status === 1) return 'bg-green-100 text-green-800'
  if (status === 2 || status === 5) return 'bg-rose-100 text-rose-800'
  if (status === 0 || status === 3) return 'bg-amber-100 text-amber-800'
  return 'bg-slate-100 text-slate-700'
})

const canSubmitBind = computed(() => {
  const status = bindRelation.value?.bindStatus
  return !!lakalaReceiver.value && (status === undefined || status === null || status === 2 || status === 4)
})

const bindActionText = computed(() => {
  if (bindRelation.value?.bindStatus === 2) return '重新提交绑定'
  if (bindRelation.value?.bindStatus === 4) return '发起分账绑定'
  return '发起分账绑定'
})

// 获取流水类型样式
const getFlowTypeClass = (type) => {
  const classMap = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-red-100 text-red-800',
    3: 'bg-amber-100 text-amber-800',
    4: 'bg-blue-100 text-blue-800'
  }
  return classMap[type] || 'bg-slate-100 text-slate-800'
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return new Date(time).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 获取证件类型文本
const getCertificateTypeText = (type) => {
  const map = {
    '17': '身份证',
    '18': '护照',
    '19': '港澳居民来往内地通行证',
    '20': '台湾居民来往内地通行证',
    '01': '身份证',
    '02': '营业执照',
    '03': '统一社会信用代码'
  }
  return map[type] || '-'
}

// 获取法人证件类型文本
const getLegalCertTypeText = (type) => {
  const map = {
    '17': '身份证',
    '18': '护照',
    '19': '港澳居民来往内地通行证',
    '20': '台湾居民来往内地通行证',
    '01': '身份证',
    '02': '护照'
  }
  return map[type] || '-'
}

// 加载账户信息
const loadAccountData = async () => {
  if (!props.referrerId) return
  loading.value = true
  try {
    accountData.value = await getReferrerAccount(props.referrerId)
  } catch (error) {
    console.error('获取账户信息失败:', error)
  } finally {
    loading.value = false
  }
}

// 加载拉卡拉分账接收方账户
const loadLakalaReceiver = async () => {
  if (!props.referrerId) return
  bankLoading.value = true
  try {
    lakalaReceiver.value = await getReferrerLakalaReceiver(props.referrerId)
    await loadDefaultBindRelation()
  } catch (error) {
    console.error('获取分账账户失败:', error)
    lakalaReceiver.value = null
    bindRelation.value = null
  } finally {
    bankLoading.value = false
  }
}

const loadDefaultBindRelation = async () => {
  if (!props.referrerId) return
  bindLoading.value = true
  try {
    bindRelation.value = await getDefaultReferrerFranchiseeBind(props.referrerId)
  } catch (error) {
    console.error('获取分账绑定状态失败:', error)
    bindRelation.value = null
  } finally {
    bindLoading.value = false
  }
}

// 创建分账账户
const handleCreateLakala = () => {
  editingLakala.value = null
  showLakalaForm.value = true
}

// 编辑分账账户
const handleEditLakala = () => {
  editingLakala.value = { ...lakalaReceiver.value }
  showLakalaForm.value = true
}

const handleOpenBindModal = () => {
  if (!lakalaReceiver.value) return
  showBindModal.value = true
}

// 分账账户操作成功
const handleLakalaSuccess = async (payload) => {
  await loadLakalaReceiver()
  if (payload?.continueBind && lakalaReceiver.value) {
    showBindModal.value = true
  }
}

const handleBindSuccess = async () => {
  await loadLakalaReceiver()
}

// 加载流水列表
const loadFlowList = async () => {
  if (!props.referrerId) return
  flowLoading.value = true
  try {
    const data = await getReferrerFlowList(props.referrerId, {
      pageNum: flowPagination.current,
      pageSize: flowPagination.size,
      referrerId: props.referrerId,
      flowType: flowQuery.flowType || undefined,
      startDate: flowQuery.startDate || undefined,
      endDate: flowQuery.endDate || undefined
    })
    flowList.value = data.records || []
    flowTotal.value = data.total || 0
    flowPagination.pages = data.pages || 1
  } catch (error) {
    console.error('获取流水列表失败:', error)
    flowList.value = []
  } finally {
    flowLoading.value = false
  }
}

// 流水搜索
const handleFlowSearch = () => {
  flowPagination.current = 1
  loadFlowList()
}

// 流水分页
const handleFlowPageChange = (page) => {
  if (page < 1 || page > flowPagination.pages) return
  flowPagination.current = page
  loadFlowList()
}

const handleFlowPageSizeChange = (size) => {
  flowPagination.size = size
  flowPagination.current = 1
  loadFlowList()
}

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false)
}

// 监听 Tab 切换
watch(activeTab, (tab) => {
  if (tab === 'account' && !accountData.value.id) {
    loadAccountData()
  } else if (tab === 'bank' && !lakalaReceiver.value) {
    loadLakalaReceiver()
  } else if (tab === 'flow' && flowList.value.length === 0) {
    loadFlowList()
  }
})

// 监听弹窗显示
watch(() => props.visible, (val) => {
  if (val) {
    // 重置状态
    accountData.value = {}
    lakalaReceiver.value = null
    bindRelation.value = null
    flowList.value = []
    showBindModal.value = false
    flowPagination.current = 1
    flowQuery.flowType = ''
    flowQuery.startDate = ''
    flowQuery.endDate = ''
    activeTab.value = 'account'
    // 加载数据
    loadAccountData()
  }
})
</script>
